"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, ReactNode, useEffect, useState } from "react";
import { useResolvedTheme } from "@/lib/hooks/use-resolved-theme";

interface ParallaxVisualsProps {
  children: ReactNode;
}

export function ParallaxVisuals({ children }: ParallaxVisualsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [heroHeight, setHeroHeight] = useState(700);
  const [isMobile, setIsMobile] = useState(false);

  // Use global scroll (not target-relative) so transforms begin at scroll pixel 0
  const { scrollY } = useScroll();

  // Map absolute scrollY to transforms — starts changing immediately from y=0
  const textY = useTransform(scrollY, [0, heroHeight], ["0%", "50%"]);
  const opacity = useTransform(scrollY, [0, heroHeight * 0.7], [1, 0]);

  const { mounted } = useResolvedTheme();

  useEffect(() => {
    // Detect mobile to disable parallax for smooth scrolling
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    // Measure actual hero height for transform range
    if (ref.current) {
      setHeroHeight(ref.current.offsetHeight);
    }
    window.scrollTo(0, 0);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    /*
     * Outer div — clips horizontal overflow only.
     * overflowX:"clip" never becomes a scroll container (unlike "hidden"),
     * so the browser doesn't capture scroll events on this element.
     */
    <div className="w-full" style={{ overflowX: "clip" }}>
      {/*
       * ref element — explicit h-screen so the measured heroHeight is consistent.
       * No overflow set here so browser never treats it as scrollable.
       */}
      <div
        ref={ref}
        className="relative flex h-screen min-h-[700px] w-full items-center justify-center bg-transparent"
      >
        {/* On mobile: no parallax transform to keep scrolling smooth */}
        {mounted && isMobile ? (
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
            {children}
          </div>
        ) : (
          <motion.div
            className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32"
            style={{ y: textY, opacity }}
          >
            {children}
          </motion.div>
        )}
      </div>
    </div>
  );
}
